import { PreEventKey } from "@clockwise/schema";
import { compact } from "lodash";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";

export interface OutlookAttendee {
  emailAddress: string;
  displayName: string;
  recipientType: string;
}

export const flexTooltipMessage =
  "Enable Clockwise to move this meeting to the best time for you and all attendees. For example, Clockwise may move this meeting to maximize your Focus Time, or to resolve a conflict for someone.";

export const useOutlookNewEventData = (calendarId?: string) => {
  const [requiredAttendees, setRequiredAttendees] = useState<OutlookAttendee[]>([]);
  const [optionalAttendees, setOptionalAttendees] = useState<OutlookAttendee[]>([]);
  const [startTime, setStartTime] = useState<Date>();
  const [endTime, setEndTime] = useState<Date>();

  useEffect(() => {
    const getAndSetStartAndEndTime = () => {
      window.Office.context.mailbox.item?.start.getAsync((date) => {
        setStartTime(new Date(date.value.valueOf()));
      });
      window.Office.context.mailbox.item?.end.getAsync((date) => {
        setEndTime(new Date(date.value.valueOf()));
      });
    };

    const getAndSetAttendees = () => {
      window.Office.context.mailbox.item?.requiredAttendees.getAsync((attendees) => {
        setRequiredAttendees(attendees.value);
      });
      window.Office.context.mailbox.item?.optionalAttendees.getAsync((attendees) => {
        setOptionalAttendees(attendees.value);
      });
    };

    // Call them both once to initialize
    getAndSetAttendees();
    getAndSetStartAndEndTime();

    // Listen for changes to the required and optional attendees
    window.Office.context.mailbox?.item?.addHandlerAsync(
      window.Office.EventType.RecipientsChanged,
      getAndSetAttendees,
    );

    // Listen for changes to the start time and end time
    window.Office.context.mailbox.item?.addHandlerAsync(
      window.Office.EventType.AppointmentTimeChanged,
      getAndSetStartAndEndTime,
    );

    return () => {
      window.Office.context.mailbox?.item?.removeHandlerAsync(
        window.Office.EventType.RecipientsChanged,
      );
      window.Office.context.mailbox?.item?.removeHandlerAsync(
        window.Office.EventType.AppointmentTimeChanged,
      );
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const calendarIds = useMemo(() => {
    const calendarIds = new Set<string>();
    requiredAttendees.forEach((attendee) => {
      if (attendee && attendee.emailAddress) {
        calendarIds.add(attendee.emailAddress);
      }
    });
    optionalAttendees.forEach((attendee) => {
      if (attendee && attendee.emailAddress) {
        calendarIds.add(attendee.emailAddress);
      }
    });

    // To follow how our Gcal code handles this, only add current user's calendar id if there are no attendees
    if (calendarIds.size && calendarId) {
      calendarIds.add(calendarId);
    }

    // Add current user's calendar id
    return Array.from(calendarIds);
  }, [calendarId, optionalAttendees, requiredAttendees]);

  return {
    requiredAttendees,
    optionalAttendees,
    startTime,
    endTime,
    calendarIds,
  };
};

export const combineRequiredAndOptionalAttendees = (
  requiredAttendees: OutlookAttendee[],
  optionalAttendees: OutlookAttendee[],
) => [
  ...compact(
    requiredAttendees.map((attendee) => {
      if (attendee && attendee.emailAddress) {
        return attendee.emailAddress;
      }
      return null;
    }),
  ),
  ...compact(
    optionalAttendees.map((attendee) => {
      if (attendee && attendee.emailAddress) {
        return attendee.emailAddress;
      }
      return null;
    }),
  ),
];

export const getOutlookEventKey = (
  startTime: Date,
  endTime: Date,
  attendees: string[],
  title?: string,
) => {
  const startDateTime = DateTime.fromMillis(startTime.valueOf()); // TODO - this is not timezone aware
  const endDateTime = DateTime.fromMillis(endTime.valueOf()); // TODO - this is not timezone aware

  const startHoursMinutesAmPm = startDateTime.toFormat("h:mm a").replaceAll(" ", "").toLowerCase();
  const endHoursMinutesAmPm = endDateTime.toFormat("h:mm a").replaceAll(" ", "").toLowerCase();

  // Remove -'s from strings
  const preEventKey: PreEventKey = {
    startTime: {
      date: startDateTime.toISODate().replaceAll("-", ""),
      time: startHoursMinutesAmPm,
    },
    endTime: { date: endDateTime.toISODate().replaceAll("-", ""), time: endHoursMinutesAmPm },
    attendees,
    title, // Set undefined rather than null. Null or empty string will result in the BE trying to match null or empty title rather than not matching at all.
  };

  return preEventKey;
};
